import React, { useCallback, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Checkbox,
    Box,
} from '@mui/material';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import SeoServices from '@/services/main/seoServices';
import { pageNumberSeo } from '@/models/seo';
import Content from '@/app/[locale]/(user-page)/terms-and-service/components/Content';

interface PopupAcceptTermServiceProps {
    onSubmit: () => void; // Hàm callback khi người dùng đồng ý
    open: boolean;
    setOpen: (open: boolean) => void;
}
const PopupAcceptTermService = ({ open, setOpen, onSubmit }: PopupAcceptTermServiceProps) => {
    const t = useTranslations("messages");
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const locale = useLocale();
    const handleScroll = useCallback((event: any) => {
        const target = event.currentTarget;
        const atBottom = Math.ceil(target.scrollTop + target.clientHeight) >= Math.floor(target.scrollHeight) - 2;
        setScrolledToBottom(atBottom);
    }, []);

    // Hàm xử lý khi check vào ô đồng ý
    const handleAgreeChange = (event: any) => {
        setAgreed(event.target.checked);
    };

    // Hàm xử lý khi người dùng nhấn nút đồng ý - >đóng popup<
    const handleClose = () => {
        // Logic khi người dùng nhấn đồng ý
        if (agreed) {
            onSubmit(); // Gọi hàm callback để xử lý logic tiếp theo
            setAgreed(false);
            setScrolledToBottom(false);
            setOpen(false);
        }
    };

    // Hàm xử lý khi người dùng nhấn từ chối
    const handleDecline = () => {
        // Logic khi người dùng nhấn từ chối
        setAgreed(false);
        setScrolledToBottom(false);
        setOpen(false);
    };
    const { data: termsContent } = useQuery({
        queryKey: ['terms-content', locale],
        queryFn: async () => {
            const response = await SeoServices.getPrivacyPolicy(
                pageNumberSeo.TOS,
                locale
            );
            return response.data.content;
        },
        staleTime: 5 * 60 * 1000, // Cache 5 phút
        refetchOnWindowFocus: false,
    });

    return (
        <Dialog open={open} scroll="paper"
            sx={{
                '& .MuiDialog-paper': {
                    overflow: 'auto', // Tắt overflow cho Dialog paper
                    maxWidth: '750px', // Giới hạn chiều rộng tối đa
                }
            }}
        >
            {/* <div style={{ maxHeight: '70vh', overflow: 'auto' }}></div> */}
            <DialogTitle
                sx={{
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                    fontWeight: 600,
                }}
                translation-key="popup_terms_title"
            >
                {t("popup_terms_title")}
                <Box
                    sx={{
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        color: '#888',
                    }}
                    translation-key="popup_terms_subtitle"
                >
                    {t("popup_terms_subtitle")}
                </Box>
            </DialogTitle>

            <DialogContent
                dividers={true}
                onScroll={handleScroll}
                sx={{
                    borderTop: '1.5px solid rgba(0, 0, 0, 0.12)',
                    borderBottom: '1.5px solid rgba(0, 0, 0, 0.12)',
                    height: { xs: '40vh', sm: '400px' }, // Responsive height: 60vh on mobile, 400px on desktop
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    '&&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: '6px',
                    },
                    '&&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
                    // Dành cho Firefox (không cần thay đổi)
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0, 0, 0, 0.4) transparent',
                }}
            >
                <DialogContentText component="div" tabIndex={-1}>
                    <Box className={`markdown-content`}>
                        <Content content={termsContent || ""} />
                    </Box>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', alignItems: 'flex-start', padding: '16px 24px' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={agreed}
                            onChange={handleAgreeChange}
                            disabled={!scrolledToBottom} // Vô hiệu hóa cho đến khi cuộn xuống cuối
                        />
                    }
                    translation-key="popup_terms_agree_label"
                    label={t("popup_terms_agree_label")}
                    sx={{
                        '& .MuiFormControlLabel-label': {
                            fontSize: {
                                xs: '12px', // mobile
                                sm: '14px', // desktop mặc định
                            },
                            fontWeight: 500,
                        }
                    }}
                />
                <Button
                    onClick={handleClose}
                    variant="contained"
                    disabled={!agreed} // Vô hiệu hóa cho đến khi check và cuộn xuống cuối
                    sx={{
                        marginTop: '8px',
                        marginLeft: '0px !important',
                        width: '100%',
                        backgroundColor: 'var(--cimigo-blue)',
                        fontWeight: 600,
                    }}
                    className="popup-terms-accept-button"
                    translation-key="popup_terms_accept_btn"
                >
                    {t("popup_terms_accept_btn")}
                </Button>
                <Button
                    onClick={handleDecline}
                    variant="outlined"
                    sx={{
                        marginLeft: '0px !important',
                        marginTop: '8px',
                        width: '100%',
                        backgroundColor: 'white',
                        color: 'var(--cimigo-blue)',
                        fontWeight: 600,
                        borderWidth: '2px',
                        borderColor: 'var(--cimigo-blue)',
                        '&:hover': {
                            borderColor: 'var(--cimigo-blue)',
                            backgroundColor: 'rgba(0, 82, 204, 0.04)', // Optional: subtle blue hover
                        }
                    }}
                    className="popup-terms-decline-button"
                    translation-key="popup_terms_decline_btn"
                >
                    {t("popup_terms_decline_btn")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default PopupAcceptTermService;
